import * as z from 'zod';

export const CreateInventoryItemRequest = z.object({
  inventoryItem: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Inventory item ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the inventory item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the inventory item.\n\nIgnored when creating an inventory item.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the inventory item was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the inventory item was last updated.')
          .optional()
          .nullable(),
        variantId: z
          .string()
          .describe('Variant ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          ),
        locationId: z
          .string()
          .describe(
            "Stores location ID. If not specified when creating an inventory item, the store's default location is used."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        productId: z.string().describe('Product ID.').min(1).max(36),
        trackQuantity: z
          .boolean()
          .describe('Whether the quantity is being tracked.')
          .optional(),
        availabilityStatus: z
          .enum(['OUT_OF_STOCK', 'IN_STOCK', 'PREORDER'])
          .optional(),
        preorderInfo: z
          .object({
            enabled: z
              .boolean()
              .describe(
                "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
              )
              .optional()
              .nullable(),
            message: z
              .string()
              .describe(
                'Message displayed to customers when the item is out of stock and preorder is enabled.'
              )
              .min(1)
              .max(250)
              .optional()
              .nullable(),
            limit: z
              .number()
              .int()
              .describe(
                'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
              )
              .min(0)
              .max(100000)
              .optional()
              .nullable(),
            counter: z
              .number()
              .int()
              .describe(
                'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
              )
              .min(0)
              .max(99999)
              .optional()
              .nullable(),
            quantity: z
              .number()
              .int()
              .describe(
                'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Item preorder info.\n\nPreorder settings are configured per inventory item, so each variant-location combination can have its own preorder configuration.\n\n> **Note:** The product entity's `inventory.preorderStatus` and `inventory.preorderAvailability` fields reflect only the default location's preorder state."
          )
          .optional(),
        product: z
          .object({
            name: z
              .string()
              .describe('Product name.')
              .max(80)
              .optional()
              .nullable(),
            directCategoryIds: z.array(z.string()).max(2000).optional(),
            variantName: z
              .string()
              .describe('Variant name.')
              .min(1)
              .max(250)
              .optional()
              .nullable(),
            variantSku: z
              .string()
              .describe('Variant SKU (stock keeping unit).')
              .min(1)
              .max(40)
              .optional()
              .nullable(),
            variantVisible: z
              .boolean()
              .describe('Whether the variant is visible in the store.')
              .optional()
              .nullable(),
          })
          .describe('Associated product and variant details.')
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the inventory item object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
      }),
      z.xor([
        z.object({
          quantity: z.never().optional(),
          inStock: z
            .boolean()
            .describe(
              "Indicates that inventory is tracked by status rather than quantity.\n\nWhen set to `true`, the item is marked as available for sale without tracking exact quantities.\nWhen set to `false`, the item is marked as out of stock.\nThis tracking method is useful for made-to-order products or items with unlimited inventory.\n\nWhen using this tracking method, `trackQuantity` is `false` and preorder limits aren't supported."
            ),
        }),
        z.object({
          inStock: z.never().optional(),
          quantity: z
            .number()
            .int()
            .describe(
              'Indicates that inventory is tracked by quantity.\n\nSet this field to the number of items currently in stock.\nThis tracking method is useful when you need to know exactly how many items are available.\n\nWhen using this tracking method, `trackQuantity` is `true`.\nQuantity can be negative when inventory is decremented for an order that has already been paid.'
            )
            .min(-99999)
            .max(99999),
        }),
      ])
    )
    .describe('Inventory item to create.'),
});
export const CreateInventoryItemResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Inventory item ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number, which increments by 1 each time the inventory item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the inventory item.\n\nIgnored when creating an inventory item.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the inventory item was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the inventory item was last updated.')
      .optional()
      .nullable(),
    variantId: z
      .string()
      .describe('Variant ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional(),
    locationId: z
      .string()
      .describe(
        "Stores location ID. If not specified when creating an inventory item, the store's default location is used."
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    productId: z.string().describe('Product ID.').min(1).max(36).optional(),
    trackQuantity: z
      .boolean()
      .describe('Whether the quantity is being tracked.')
      .optional(),
    availabilityStatus: z
      .enum(['OUT_OF_STOCK', 'IN_STOCK', 'PREORDER'])
      .describe('Inventory item availability status.')
      .optional(),
    preorderInfo: z
      .object({
        enabled: z
          .boolean()
          .describe(
            "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
          )
          .optional()
          .nullable(),
        message: z
          .string()
          .describe(
            'Message displayed to customers when the item is out of stock and preorder is enabled.'
          )
          .min(1)
          .max(250)
          .optional()
          .nullable(),
        limit: z
          .number()
          .int()
          .describe(
            'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
          )
          .min(0)
          .max(100000)
          .optional()
          .nullable(),
        counter: z
          .number()
          .int()
          .describe(
            'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
          )
          .min(0)
          .max(99999)
          .optional()
          .nullable(),
        quantity: z
          .number()
          .int()
          .describe(
            'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
          )
          .optional()
          .nullable(),
      })
      .describe(
        "Item preorder info.\n\nPreorder settings are configured per inventory item, so each variant-location combination can have its own preorder configuration.\n\n> **Note:** The product entity's `inventory.preorderStatus` and `inventory.preorderAvailability` fields reflect only the default location's preorder state."
      )
      .optional(),
    product: z
      .object({
        name: z
          .string()
          .describe('Product name.')
          .max(80)
          .optional()
          .nullable(),
        directCategoryIds: z.array(z.string()).max(2000).optional(),
        variantName: z
          .string()
          .describe('Variant name.')
          .min(1)
          .max(250)
          .optional()
          .nullable(),
        variantSku: z
          .string()
          .describe('Variant SKU (stock keeping unit).')
          .min(1)
          .max(40)
          .optional()
          .nullable(),
        variantVisible: z
          .boolean()
          .describe('Whether the variant is visible in the store.')
          .optional()
          .nullable(),
      })
      .describe('Associated product and variant details.')
      .optional(),
    extendedFields: z
      .object({
        namespaces: z
          .record(z.string(), z.record(z.string(), z.any()))
          .describe(
            'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
          )
          .optional(),
      })
      .describe(
        'Custom field data for the inventory item object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
      )
      .optional(),
  }),
  z.xor([
    z.object({ inStock: z.never().optional(), quantity: z.never().optional() }),
    z.object({
      quantity: z.never().optional(),
      inStock: z
        .boolean()
        .describe(
          "Indicates that inventory is tracked by status rather than quantity.\n\nWhen set to `true`, the item is marked as available for sale without tracking exact quantities.\nWhen set to `false`, the item is marked as out of stock.\nThis tracking method is useful for made-to-order products or items with unlimited inventory.\n\nWhen using this tracking method, `trackQuantity` is `false` and preorder limits aren't supported."
        ),
    }),
    z.object({
      inStock: z.never().optional(),
      quantity: z
        .number()
        .int()
        .describe(
          'Indicates that inventory is tracked by quantity.\n\nSet this field to the number of items currently in stock.\nThis tracking method is useful when you need to know exactly how many items are available.\n\nWhen using this tracking method, `trackQuantity` is `true`.\nQuantity can be negative when inventory is decremented for an order that has already been paid.'
        )
        .min(-99999)
        .max(99999),
    }),
  ])
);
export const BulkCreateInventoryItemsRequest = z.object({
  inventoryItems: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Inventory item ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the inventory item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the inventory item.\n\nIgnored when creating an inventory item.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the inventory item was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the inventory item was last updated.')
            .optional()
            .nullable(),
          variantId: z
            .string()
            .describe('Variant ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            ),
          locationId: z
            .string()
            .describe(
              "Stores location ID. If not specified when creating an inventory item, the store's default location is used."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          productId: z.string().describe('Product ID.').min(1).max(36),
          trackQuantity: z
            .boolean()
            .describe('Whether the quantity is being tracked.')
            .optional(),
          availabilityStatus: z
            .enum(['OUT_OF_STOCK', 'IN_STOCK', 'PREORDER'])
            .optional(),
          preorderInfo: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
                )
                .optional()
                .nullable(),
              message: z
                .string()
                .describe(
                  'Message displayed to customers when the item is out of stock and preorder is enabled.'
                )
                .min(1)
                .max(250)
                .optional()
                .nullable(),
              limit: z
                .number()
                .int()
                .describe(
                  'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
                )
                .min(0)
                .max(100000)
                .optional()
                .nullable(),
              counter: z
                .number()
                .int()
                .describe(
                  'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
                )
                .min(0)
                .max(99999)
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe(
                  'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              "Item preorder info.\n\nPreorder settings are configured per inventory item, so each variant-location combination can have its own preorder configuration.\n\n> **Note:** The product entity's `inventory.preorderStatus` and `inventory.preorderAvailability` fields reflect only the default location's preorder state."
            )
            .optional(),
          product: z
            .object({
              name: z
                .string()
                .describe('Product name.')
                .max(80)
                .optional()
                .nullable(),
              directCategoryIds: z.array(z.string()).max(2000).optional(),
              variantName: z
                .string()
                .describe('Variant name.')
                .min(1)
                .max(250)
                .optional()
                .nullable(),
              variantSku: z
                .string()
                .describe('Variant SKU (stock keeping unit).')
                .min(1)
                .max(40)
                .optional()
                .nullable(),
              variantVisible: z
                .boolean()
                .describe('Whether the variant is visible in the store.')
                .optional()
                .nullable(),
            })
            .describe('Associated product and variant details.')
            .optional(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe(
              'Custom field data for the inventory item object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
            )
            .optional(),
        }),
        z.xor([
          z.object({
            quantity: z.never().optional(),
            inStock: z
              .boolean()
              .describe(
                "Indicates that inventory is tracked by status rather than quantity.\n\nWhen set to `true`, the item is marked as available for sale without tracking exact quantities.\nWhen set to `false`, the item is marked as out of stock.\nThis tracking method is useful for made-to-order products or items with unlimited inventory.\n\nWhen using this tracking method, `trackQuantity` is `false` and preorder limits aren't supported."
              ),
          }),
          z.object({
            inStock: z.never().optional(),
            quantity: z
              .number()
              .int()
              .describe(
                'Indicates that inventory is tracked by quantity.\n\nSet this field to the number of items currently in stock.\nThis tracking method is useful when you need to know exactly how many items are available.\n\nWhen using this tracking method, `trackQuantity` is `true`.\nQuantity can be negative when inventory is decremented for an order that has already been paid.'
              )
              .min(-99999)
              .max(99999),
          }),
        ])
      )
    )
    .min(1)
    .max(1000),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to return the full inventory item entities in the response.\n\nDefault: `false`'
        )
        .optional(),
    })
    .optional(),
});
export const BulkCreateInventoryItemsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Bulk action metadata for inventory item.')
          .optional(),
        item: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Inventory item ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the inventory item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the inventory item.\n\nIgnored when creating an inventory item.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Date and time the inventory item was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the inventory item was last updated.')
                .optional()
                .nullable(),
              variantId: z
                .string()
                .describe('Variant ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              locationId: z
                .string()
                .describe(
                  "Stores location ID. If not specified when creating an inventory item, the store's default location is used."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              productId: z
                .string()
                .describe('Product ID.')
                .min(1)
                .max(36)
                .optional(),
              trackQuantity: z
                .boolean()
                .describe('Whether the quantity is being tracked.')
                .optional(),
              availabilityStatus: z
                .enum(['OUT_OF_STOCK', 'IN_STOCK', 'PREORDER'])
                .describe('Inventory item availability status.')
                .optional(),
              preorderInfo: z
                .object({
                  enabled: z
                    .boolean()
                    .describe(
                      "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
                    )
                    .optional()
                    .nullable(),
                  message: z
                    .string()
                    .describe(
                      'Message displayed to customers when the item is out of stock and preorder is enabled.'
                    )
                    .min(1)
                    .max(250)
                    .optional()
                    .nullable(),
                  limit: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
                    )
                    .min(0)
                    .max(100000)
                    .optional()
                    .nullable(),
                  counter: z
                    .number()
                    .int()
                    .describe(
                      'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
                    )
                    .min(0)
                    .max(99999)
                    .optional()
                    .nullable(),
                  quantity: z
                    .number()
                    .int()
                    .describe(
                      'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Item preorder info.\n\nPreorder settings are configured per inventory item, so each variant-location combination can have its own preorder configuration.\n\n> **Note:** The product entity's `inventory.preorderStatus` and `inventory.preorderAvailability` fields reflect only the default location's preorder state."
                )
                .optional(),
              product: z
                .object({
                  name: z
                    .string()
                    .describe('Product name.')
                    .max(80)
                    .optional()
                    .nullable(),
                  directCategoryIds: z.array(z.string()).max(2000).optional(),
                  variantName: z
                    .string()
                    .describe('Variant name.')
                    .min(1)
                    .max(250)
                    .optional()
                    .nullable(),
                  variantSku: z
                    .string()
                    .describe('Variant SKU (stock keeping unit).')
                    .min(1)
                    .max(40)
                    .optional()
                    .nullable(),
                  variantVisible: z
                    .boolean()
                    .describe('Whether the variant is visible in the store.')
                    .optional()
                    .nullable(),
                })
                .describe('Associated product and variant details.')
                .optional(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Custom field data for the inventory item object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                inStock: z.never().optional(),
                quantity: z.never().optional(),
              }),
              z.object({
                quantity: z.never().optional(),
                inStock: z
                  .boolean()
                  .describe(
                    "Indicates that inventory is tracked by status rather than quantity.\n\nWhen set to `true`, the item is marked as available for sale without tracking exact quantities.\nWhen set to `false`, the item is marked as out of stock.\nThis tracking method is useful for made-to-order products or items with unlimited inventory.\n\nWhen using this tracking method, `trackQuantity` is `false` and preorder limits aren't supported."
                  ),
              }),
              z.object({
                inStock: z.never().optional(),
                quantity: z
                  .number()
                  .int()
                  .describe(
                    'Indicates that inventory is tracked by quantity.\n\nSet this field to the number of items currently in stock.\nThis tracking method is useful when you need to know exactly how many items are available.\n\nWhen using this tracking method, `trackQuantity` is `true`.\nQuantity can be negative when inventory is decremented for an order that has already been paid.'
                  )
                  .min(-99999)
                  .max(99999),
              }),
            ])
          )
          .describe(
            'Full inventory item entity.\n\nReturned only if `returnEntity: true` is passed in the request.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(1000)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const GetInventoryItemRequest = z.object({
  inventoryItemId: z
    .string()
    .describe('Inventory item ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetInventoryItemResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Inventory item ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number, which increments by 1 each time the inventory item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the inventory item.\n\nIgnored when creating an inventory item.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the inventory item was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the inventory item was last updated.')
      .optional()
      .nullable(),
    variantId: z
      .string()
      .describe('Variant ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional(),
    locationId: z
      .string()
      .describe(
        "Stores location ID. If not specified when creating an inventory item, the store's default location is used."
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    productId: z.string().describe('Product ID.').min(1).max(36).optional(),
    trackQuantity: z
      .boolean()
      .describe('Whether the quantity is being tracked.')
      .optional(),
    availabilityStatus: z
      .enum(['OUT_OF_STOCK', 'IN_STOCK', 'PREORDER'])
      .describe('Inventory item availability status.')
      .optional(),
    preorderInfo: z
      .object({
        enabled: z
          .boolean()
          .describe(
            "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
          )
          .optional()
          .nullable(),
        message: z
          .string()
          .describe(
            'Message displayed to customers when the item is out of stock and preorder is enabled.'
          )
          .min(1)
          .max(250)
          .optional()
          .nullable(),
        limit: z
          .number()
          .int()
          .describe(
            'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
          )
          .min(0)
          .max(100000)
          .optional()
          .nullable(),
        counter: z
          .number()
          .int()
          .describe(
            'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
          )
          .min(0)
          .max(99999)
          .optional()
          .nullable(),
        quantity: z
          .number()
          .int()
          .describe(
            'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
          )
          .optional()
          .nullable(),
      })
      .describe(
        "Item preorder info.\n\nPreorder settings are configured per inventory item, so each variant-location combination can have its own preorder configuration.\n\n> **Note:** The product entity's `inventory.preorderStatus` and `inventory.preorderAvailability` fields reflect only the default location's preorder state."
      )
      .optional(),
    product: z
      .object({
        name: z
          .string()
          .describe('Product name.')
          .max(80)
          .optional()
          .nullable(),
        directCategoryIds: z.array(z.string()).max(2000).optional(),
        variantName: z
          .string()
          .describe('Variant name.')
          .min(1)
          .max(250)
          .optional()
          .nullable(),
        variantSku: z
          .string()
          .describe('Variant SKU (stock keeping unit).')
          .min(1)
          .max(40)
          .optional()
          .nullable(),
        variantVisible: z
          .boolean()
          .describe('Whether the variant is visible in the store.')
          .optional()
          .nullable(),
      })
      .describe('Associated product and variant details.')
      .optional(),
    extendedFields: z
      .object({
        namespaces: z
          .record(z.string(), z.record(z.string(), z.any()))
          .describe(
            'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
          )
          .optional(),
      })
      .describe(
        'Custom field data for the inventory item object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
      )
      .optional(),
  }),
  z.xor([
    z.object({ inStock: z.never().optional(), quantity: z.never().optional() }),
    z.object({
      quantity: z.never().optional(),
      inStock: z
        .boolean()
        .describe(
          "Indicates that inventory is tracked by status rather than quantity.\n\nWhen set to `true`, the item is marked as available for sale without tracking exact quantities.\nWhen set to `false`, the item is marked as out of stock.\nThis tracking method is useful for made-to-order products or items with unlimited inventory.\n\nWhen using this tracking method, `trackQuantity` is `false` and preorder limits aren't supported."
        ),
    }),
    z.object({
      inStock: z.never().optional(),
      quantity: z
        .number()
        .int()
        .describe(
          'Indicates that inventory is tracked by quantity.\n\nSet this field to the number of items currently in stock.\nThis tracking method is useful when you need to know exactly how many items are available.\n\nWhen using this tracking method, `trackQuantity` is `true`.\nQuantity can be negative when inventory is decremented for an order that has already been paid.'
        )
        .min(-99999)
        .max(99999),
    }),
  ])
);
export const UpdateInventoryItemRequest = z.object({
  _id: z
    .string()
    .describe('Inventory item ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  inventoryItem: z
    .intersection(
      z.object({
        _id: z
          .string()
          .describe('Inventory item ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the inventory item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the inventory item.\n\nIgnored when creating an inventory item.'
          ),
        _createdDate: z
          .date()
          .describe('Date and time the inventory item was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the inventory item was last updated.')
          .optional()
          .nullable(),
        variantId: z
          .string()
          .describe('Variant ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        locationId: z
          .string()
          .describe(
            "Stores location ID. If not specified when creating an inventory item, the store's default location is used."
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        productId: z.string().describe('Product ID.').min(1).max(36).optional(),
        trackQuantity: z
          .boolean()
          .describe('Whether the quantity is being tracked.')
          .optional(),
        availabilityStatus: z
          .enum(['OUT_OF_STOCK', 'IN_STOCK', 'PREORDER'])
          .optional(),
        preorderInfo: z
          .object({
            enabled: z
              .boolean()
              .describe(
                "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
              )
              .optional()
              .nullable(),
            message: z
              .string()
              .describe(
                'Message displayed to customers when the item is out of stock and preorder is enabled.'
              )
              .min(1)
              .max(250)
              .optional()
              .nullable(),
            limit: z
              .number()
              .int()
              .describe(
                'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
              )
              .min(0)
              .max(100000)
              .optional()
              .nullable(),
            counter: z
              .number()
              .int()
              .describe(
                'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
              )
              .min(0)
              .max(99999)
              .optional()
              .nullable(),
            quantity: z
              .number()
              .int()
              .describe(
                'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
              )
              .optional()
              .nullable(),
          })
          .describe(
            "Item preorder info.\n\nPreorder settings are configured per inventory item, so each variant-location combination can have its own preorder configuration.\n\n> **Note:** The product entity's `inventory.preorderStatus` and `inventory.preorderAvailability` fields reflect only the default location's preorder state."
          )
          .optional(),
        product: z
          .object({
            name: z
              .string()
              .describe('Product name.')
              .max(80)
              .optional()
              .nullable(),
            directCategoryIds: z.array(z.string()).max(2000).optional(),
            variantName: z
              .string()
              .describe('Variant name.')
              .min(1)
              .max(250)
              .optional()
              .nullable(),
            variantSku: z
              .string()
              .describe('Variant SKU (stock keeping unit).')
              .min(1)
              .max(40)
              .optional()
              .nullable(),
            variantVisible: z
              .boolean()
              .describe('Whether the variant is visible in the store.')
              .optional()
              .nullable(),
          })
          .describe('Associated product and variant details.')
          .optional(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe(
            'Custom field data for the inventory item object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
          )
          .optional(),
      }),
      z.xor([
        z.object({
          inStock: z.never().optional(),
          quantity: z.never().optional(),
        }),
        z.object({
          quantity: z.never().optional(),
          inStock: z
            .boolean()
            .describe(
              "Indicates that inventory is tracked by status rather than quantity.\n\nWhen set to `true`, the item is marked as available for sale without tracking exact quantities.\nWhen set to `false`, the item is marked as out of stock.\nThis tracking method is useful for made-to-order products or items with unlimited inventory.\n\nWhen using this tracking method, `trackQuantity` is `false` and preorder limits aren't supported."
            ),
        }),
        z.object({
          inStock: z.never().optional(),
          quantity: z
            .number()
            .int()
            .describe(
              'Indicates that inventory is tracked by quantity.\n\nSet this field to the number of items currently in stock.\nThis tracking method is useful when you need to know exactly how many items are available.\n\nWhen using this tracking method, `trackQuantity` is `true`.\nQuantity can be negative when inventory is decremented for an order that has already been paid.'
            )
            .min(-99999)
            .max(99999),
        }),
      ])
    )
    .describe('Inventory item to update.'),
  options: z
    .object({
      reason: z.enum(['ORDER', 'MANUAL', 'REVERT_INVENTORY_CHANGE']).optional(),
    })
    .optional(),
});
export const UpdateInventoryItemResponse = z.intersection(
  z.object({
    _id: z
      .string()
      .describe('Inventory item ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    revision: z
      .string()
      .regex(/^-?\d+$/, 'Must be a valid Int64 string')
      .describe(
        'Revision number, which increments by 1 each time the inventory item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the inventory item.\n\nIgnored when creating an inventory item.'
      )
      .optional()
      .nullable(),
    _createdDate: z
      .date()
      .describe('Date and time the inventory item was created.')
      .optional()
      .nullable(),
    _updatedDate: z
      .date()
      .describe('Date and time the inventory item was last updated.')
      .optional()
      .nullable(),
    variantId: z
      .string()
      .describe('Variant ID.')
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional(),
    locationId: z
      .string()
      .describe(
        "Stores location ID. If not specified when creating an inventory item, the store's default location is used."
      )
      .regex(
        /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
        'Must be a valid GUID'
      )
      .optional()
      .nullable(),
    productId: z.string().describe('Product ID.').min(1).max(36).optional(),
    trackQuantity: z
      .boolean()
      .describe('Whether the quantity is being tracked.')
      .optional(),
    availabilityStatus: z
      .enum(['OUT_OF_STOCK', 'IN_STOCK', 'PREORDER'])
      .describe('Inventory item availability status.')
      .optional(),
    preorderInfo: z
      .object({
        enabled: z
          .boolean()
          .describe(
            "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
          )
          .optional()
          .nullable(),
        message: z
          .string()
          .describe(
            'Message displayed to customers when the item is out of stock and preorder is enabled.'
          )
          .min(1)
          .max(250)
          .optional()
          .nullable(),
        limit: z
          .number()
          .int()
          .describe(
            'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
          )
          .min(0)
          .max(100000)
          .optional()
          .nullable(),
        counter: z
          .number()
          .int()
          .describe(
            'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
          )
          .min(0)
          .max(99999)
          .optional()
          .nullable(),
        quantity: z
          .number()
          .int()
          .describe(
            'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
          )
          .optional()
          .nullable(),
      })
      .describe(
        "Item preorder info.\n\nPreorder settings are configured per inventory item, so each variant-location combination can have its own preorder configuration.\n\n> **Note:** The product entity's `inventory.preorderStatus` and `inventory.preorderAvailability` fields reflect only the default location's preorder state."
      )
      .optional(),
    product: z
      .object({
        name: z
          .string()
          .describe('Product name.')
          .max(80)
          .optional()
          .nullable(),
        directCategoryIds: z.array(z.string()).max(2000).optional(),
        variantName: z
          .string()
          .describe('Variant name.')
          .min(1)
          .max(250)
          .optional()
          .nullable(),
        variantSku: z
          .string()
          .describe('Variant SKU (stock keeping unit).')
          .min(1)
          .max(40)
          .optional()
          .nullable(),
        variantVisible: z
          .boolean()
          .describe('Whether the variant is visible in the store.')
          .optional()
          .nullable(),
      })
      .describe('Associated product and variant details.')
      .optional(),
    extendedFields: z
      .object({
        namespaces: z
          .record(z.string(), z.record(z.string(), z.any()))
          .describe(
            'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
          )
          .optional(),
      })
      .describe(
        'Custom field data for the inventory item object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
      )
      .optional(),
  }),
  z.xor([
    z.object({ inStock: z.never().optional(), quantity: z.never().optional() }),
    z.object({
      quantity: z.never().optional(),
      inStock: z
        .boolean()
        .describe(
          "Indicates that inventory is tracked by status rather than quantity.\n\nWhen set to `true`, the item is marked as available for sale without tracking exact quantities.\nWhen set to `false`, the item is marked as out of stock.\nThis tracking method is useful for made-to-order products or items with unlimited inventory.\n\nWhen using this tracking method, `trackQuantity` is `false` and preorder limits aren't supported."
        ),
    }),
    z.object({
      inStock: z.never().optional(),
      quantity: z
        .number()
        .int()
        .describe(
          'Indicates that inventory is tracked by quantity.\n\nSet this field to the number of items currently in stock.\nThis tracking method is useful when you need to know exactly how many items are available.\n\nWhen using this tracking method, `trackQuantity` is `true`.\nQuantity can be negative when inventory is decremented for an order that has already been paid.'
        )
        .min(-99999)
        .max(99999),
    }),
  ])
);
export const BulkUpdateInventoryItemsRequest = z.object({
  inventoryItems: z
    .array(
      z.object({
        inventoryItem: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Inventory item ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                ),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the inventory item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the inventory item.\n\nIgnored when creating an inventory item.'
                ),
              _createdDate: z
                .date()
                .describe('Date and time the inventory item was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the inventory item was last updated.')
                .optional()
                .nullable(),
              variantId: z
                .string()
                .describe('Variant ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              locationId: z
                .string()
                .describe(
                  "Stores location ID. If not specified when creating an inventory item, the store's default location is used."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              productId: z
                .string()
                .describe('Product ID.')
                .min(1)
                .max(36)
                .optional(),
              trackQuantity: z
                .boolean()
                .describe('Whether the quantity is being tracked.')
                .optional(),
              availabilityStatus: z
                .enum(['OUT_OF_STOCK', 'IN_STOCK', 'PREORDER'])
                .optional(),
              preorderInfo: z
                .object({
                  enabled: z
                    .boolean()
                    .describe(
                      "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
                    )
                    .optional()
                    .nullable(),
                  message: z
                    .string()
                    .describe(
                      'Message displayed to customers when the item is out of stock and preorder is enabled.'
                    )
                    .min(1)
                    .max(250)
                    .optional()
                    .nullable(),
                  limit: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
                    )
                    .min(0)
                    .max(100000)
                    .optional()
                    .nullable(),
                  counter: z
                    .number()
                    .int()
                    .describe(
                      'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
                    )
                    .min(0)
                    .max(99999)
                    .optional()
                    .nullable(),
                  quantity: z
                    .number()
                    .int()
                    .describe(
                      'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Item preorder info.\n\nPreorder settings are configured per inventory item, so each variant-location combination can have its own preorder configuration.\n\n> **Note:** The product entity's `inventory.preorderStatus` and `inventory.preorderAvailability` fields reflect only the default location's preorder state."
                )
                .optional(),
              product: z
                .object({
                  name: z
                    .string()
                    .describe('Product name.')
                    .max(80)
                    .optional()
                    .nullable(),
                  directCategoryIds: z.array(z.string()).max(2000).optional(),
                  variantName: z
                    .string()
                    .describe('Variant name.')
                    .min(1)
                    .max(250)
                    .optional()
                    .nullable(),
                  variantSku: z
                    .string()
                    .describe('Variant SKU (stock keeping unit).')
                    .min(1)
                    .max(40)
                    .optional()
                    .nullable(),
                  variantVisible: z
                    .boolean()
                    .describe('Whether the variant is visible in the store.')
                    .optional()
                    .nullable(),
                })
                .describe('Associated product and variant details.')
                .optional(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Custom field data for the inventory item object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                inStock: z.never().optional(),
                quantity: z.never().optional(),
              }),
              z.object({
                quantity: z.never().optional(),
                inStock: z
                  .boolean()
                  .describe(
                    "Indicates that inventory is tracked by status rather than quantity.\n\nWhen set to `true`, the item is marked as available for sale without tracking exact quantities.\nWhen set to `false`, the item is marked as out of stock.\nThis tracking method is useful for made-to-order products or items with unlimited inventory.\n\nWhen using this tracking method, `trackQuantity` is `false` and preorder limits aren't supported."
                  ),
              }),
              z.object({
                inStock: z.never().optional(),
                quantity: z
                  .number()
                  .int()
                  .describe(
                    'Indicates that inventory is tracked by quantity.\n\nSet this field to the number of items currently in stock.\nThis tracking method is useful when you need to know exactly how many items are available.\n\nWhen using this tracking method, `trackQuantity` is `true`.\nQuantity can be negative when inventory is decremented for an order that has already been paid.'
                  )
                  .min(-99999)
                  .max(99999),
              }),
            ])
          )
          .describe('Inventory item to update.')
          .optional(),
      })
    )
    .min(1)
    .max(1000),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to return the full inventory item entities in the response.\n\nDefault: `false`'
        )
        .optional(),
      reason: z.enum(['ORDER', 'MANUAL', 'REVERT_INVENTORY_CHANGE']).optional(),
    })
    .optional(),
});
export const BulkUpdateInventoryItemsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Bulk action metadata for inventory item.')
          .optional(),
        item: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Inventory item ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the inventory item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the inventory item.\n\nIgnored when creating an inventory item.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Date and time the inventory item was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the inventory item was last updated.')
                .optional()
                .nullable(),
              variantId: z
                .string()
                .describe('Variant ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              locationId: z
                .string()
                .describe(
                  "Stores location ID. If not specified when creating an inventory item, the store's default location is used."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              productId: z
                .string()
                .describe('Product ID.')
                .min(1)
                .max(36)
                .optional(),
              trackQuantity: z
                .boolean()
                .describe('Whether the quantity is being tracked.')
                .optional(),
              availabilityStatus: z
                .enum(['OUT_OF_STOCK', 'IN_STOCK', 'PREORDER'])
                .describe('Inventory item availability status.')
                .optional(),
              preorderInfo: z
                .object({
                  enabled: z
                    .boolean()
                    .describe(
                      "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
                    )
                    .optional()
                    .nullable(),
                  message: z
                    .string()
                    .describe(
                      'Message displayed to customers when the item is out of stock and preorder is enabled.'
                    )
                    .min(1)
                    .max(250)
                    .optional()
                    .nullable(),
                  limit: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
                    )
                    .min(0)
                    .max(100000)
                    .optional()
                    .nullable(),
                  counter: z
                    .number()
                    .int()
                    .describe(
                      'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
                    )
                    .min(0)
                    .max(99999)
                    .optional()
                    .nullable(),
                  quantity: z
                    .number()
                    .int()
                    .describe(
                      'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Item preorder info.\n\nPreorder settings are configured per inventory item, so each variant-location combination can have its own preorder configuration.\n\n> **Note:** The product entity's `inventory.preorderStatus` and `inventory.preorderAvailability` fields reflect only the default location's preorder state."
                )
                .optional(),
              product: z
                .object({
                  name: z
                    .string()
                    .describe('Product name.')
                    .max(80)
                    .optional()
                    .nullable(),
                  directCategoryIds: z.array(z.string()).max(2000).optional(),
                  variantName: z
                    .string()
                    .describe('Variant name.')
                    .min(1)
                    .max(250)
                    .optional()
                    .nullable(),
                  variantSku: z
                    .string()
                    .describe('Variant SKU (stock keeping unit).')
                    .min(1)
                    .max(40)
                    .optional()
                    .nullable(),
                  variantVisible: z
                    .boolean()
                    .describe('Whether the variant is visible in the store.')
                    .optional()
                    .nullable(),
                })
                .describe('Associated product and variant details.')
                .optional(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Custom field data for the inventory item object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                inStock: z.never().optional(),
                quantity: z.never().optional(),
              }),
              z.object({
                quantity: z.never().optional(),
                inStock: z
                  .boolean()
                  .describe(
                    "Indicates that inventory is tracked by status rather than quantity.\n\nWhen set to `true`, the item is marked as available for sale without tracking exact quantities.\nWhen set to `false`, the item is marked as out of stock.\nThis tracking method is useful for made-to-order products or items with unlimited inventory.\n\nWhen using this tracking method, `trackQuantity` is `false` and preorder limits aren't supported."
                  ),
              }),
              z.object({
                inStock: z.never().optional(),
                quantity: z
                  .number()
                  .int()
                  .describe(
                    'Indicates that inventory is tracked by quantity.\n\nSet this field to the number of items currently in stock.\nThis tracking method is useful when you need to know exactly how many items are available.\n\nWhen using this tracking method, `trackQuantity` is `true`.\nQuantity can be negative when inventory is decremented for an order that has already been paid.'
                  )
                  .min(-99999)
                  .max(99999),
              }),
            ])
          )
          .describe(
            'Full inventory item entity.\n\nReturned only if `returnEntity: true` is passed in the request.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(1000)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const BulkUpdateInventoryItemsByFilterRequest = z.object({
  filter: z.record(z.string(), z.any()).describe('Filter object.'),
  options: z.object({
    inventoryItem: z
      .intersection(
        z.object({
          _id: z
            .string()
            .describe('Inventory item ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the inventory item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the inventory item.\n\nIgnored when creating an inventory item.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the inventory item was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the inventory item was last updated.')
            .optional()
            .nullable(),
          variantId: z
            .string()
            .describe('Variant ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          locationId: z
            .string()
            .describe(
              "Stores location ID. If not specified when creating an inventory item, the store's default location is used."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          productId: z
            .string()
            .describe('Product ID.')
            .min(1)
            .max(36)
            .optional(),
          trackQuantity: z
            .boolean()
            .describe('Whether the quantity is being tracked.')
            .optional(),
          availabilityStatus: z
            .enum(['OUT_OF_STOCK', 'IN_STOCK', 'PREORDER'])
            .optional(),
          preorderInfo: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
                )
                .optional()
                .nullable(),
              message: z
                .string()
                .describe(
                  'Message displayed to customers when the item is out of stock and preorder is enabled.'
                )
                .min(1)
                .max(250)
                .optional()
                .nullable(),
              limit: z
                .number()
                .int()
                .describe(
                  'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
                )
                .min(0)
                .max(100000)
                .optional()
                .nullable(),
              counter: z
                .number()
                .int()
                .describe(
                  'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
                )
                .min(0)
                .max(99999)
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe(
                  'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              "Item preorder info.\n\nPreorder settings are configured per inventory item, so each variant-location combination can have its own preorder configuration.\n\n> **Note:** The product entity's `inventory.preorderStatus` and `inventory.preorderAvailability` fields reflect only the default location's preorder state."
            )
            .optional(),
          product: z
            .object({
              name: z
                .string()
                .describe('Product name.')
                .max(80)
                .optional()
                .nullable(),
              directCategoryIds: z.array(z.string()).max(2000).optional(),
              variantName: z
                .string()
                .describe('Variant name.')
                .min(1)
                .max(250)
                .optional()
                .nullable(),
              variantSku: z
                .string()
                .describe('Variant SKU (stock keeping unit).')
                .min(1)
                .max(40)
                .optional()
                .nullable(),
              variantVisible: z
                .boolean()
                .describe('Whether the variant is visible in the store.')
                .optional()
                .nullable(),
            })
            .describe('Associated product and variant details.')
            .optional(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe(
              'Custom field data for the inventory item object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
            )
            .optional(),
        }),
        z.xor([
          z.object({
            inStock: z.never().optional(),
            quantity: z.never().optional(),
          }),
          z.object({
            quantity: z.never().optional(),
            inStock: z
              .boolean()
              .describe(
                "Indicates that inventory is tracked by status rather than quantity.\n\nWhen set to `true`, the item is marked as available for sale without tracking exact quantities.\nWhen set to `false`, the item is marked as out of stock.\nThis tracking method is useful for made-to-order products or items with unlimited inventory.\n\nWhen using this tracking method, `trackQuantity` is `false` and preorder limits aren't supported."
              ),
          }),
          z.object({
            inStock: z.never().optional(),
            quantity: z
              .number()
              .int()
              .describe(
                'Indicates that inventory is tracked by quantity.\n\nSet this field to the number of items currently in stock.\nThis tracking method is useful when you need to know exactly how many items are available.\n\nWhen using this tracking method, `trackQuantity` is `true`.\nQuantity can be negative when inventory is decremented for an order that has already been paid.'
              )
              .min(-99999)
              .max(99999),
          }),
        ])
      )
      .describe('Inventory item to update.'),
    search: z
      .object({
        mode: z.enum(['OR', 'AND']).optional(),
        expression: z
          .string()
          .describe('Search term or expression.')
          .max(100)
          .optional()
          .nullable(),
        fields: z.array(z.string()).max(20).optional(),
        fuzzy: z
          .boolean()
          .describe(
            'Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions.'
          )
          .optional(),
      })
      .describe('Free text to match in searchable fields.')
      .optional(),
  }),
});
export const BulkUpdateInventoryItemsByFilterResponse = z.object({
  jobId: z
    .string()
    .describe(
      'Job ID.\n\nPass this ID to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/introduction) to retrieve job details and metadata.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
export const DeleteInventoryItemRequest = z.object({
  inventoryItemId: z
    .string()
    .describe('Inventory item ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteInventoryItemResponse = z.object({});
export const BulkDeleteInventoryItemsRequest = z.object({
  inventoryItemIds: z.array(z.string()).min(1).max(1000),
});
export const BulkDeleteInventoryItemsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Bulk action metadata for inventory item.')
          .optional(),
      })
    )
    .min(1)
    .max(1000)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const QueryInventoryItemsRequest = z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object.\n\nLearn more about [filtering](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#filters).'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(10)
          .optional(),
      }),
      z.xor([
        z.object({
          paging: z.never().optional(),
          cursorPaging: z.never().optional(),
        }),
        z.object({
          cursorPaging: z.never().optional(),
          paging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Number of items to load.')
                .min(0)
                .max(1000)
                .optional()
                .nullable(),
              offset: z
                .number()
                .int()
                .describe('Number of items to skip in the current sort order.')
                .min(0)
                .optional()
                .nullable(),
            })
            .describe(
              'Paging options to limit and offset the number of items.'
            ),
        }),
        z.object({
          paging: z.never().optional(),
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(1000)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
            ),
        }),
      ])
    )
    .describe('Query options.'),
});
export const QueryInventoryItemsResponse = z.object({
  inventoryItems: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Inventory item ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the inventory item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the inventory item.\n\nIgnored when creating an inventory item.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the inventory item was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the inventory item was last updated.')
            .optional()
            .nullable(),
          variantId: z
            .string()
            .describe('Variant ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          locationId: z
            .string()
            .describe(
              "Stores location ID. If not specified when creating an inventory item, the store's default location is used."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          productId: z
            .string()
            .describe('Product ID.')
            .min(1)
            .max(36)
            .optional(),
          trackQuantity: z
            .boolean()
            .describe('Whether the quantity is being tracked.')
            .optional(),
          availabilityStatus: z
            .enum(['OUT_OF_STOCK', 'IN_STOCK', 'PREORDER'])
            .describe('Inventory item availability status.')
            .optional(),
          preorderInfo: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
                )
                .optional()
                .nullable(),
              message: z
                .string()
                .describe(
                  'Message displayed to customers when the item is out of stock and preorder is enabled.'
                )
                .min(1)
                .max(250)
                .optional()
                .nullable(),
              limit: z
                .number()
                .int()
                .describe(
                  'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
                )
                .min(0)
                .max(100000)
                .optional()
                .nullable(),
              counter: z
                .number()
                .int()
                .describe(
                  'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
                )
                .min(0)
                .max(99999)
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe(
                  'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              "Item preorder info.\n\nPreorder settings are configured per inventory item, so each variant-location combination can have its own preorder configuration.\n\n> **Note:** The product entity's `inventory.preorderStatus` and `inventory.preorderAvailability` fields reflect only the default location's preorder state."
            )
            .optional(),
          product: z
            .object({
              name: z
                .string()
                .describe('Product name.')
                .max(80)
                .optional()
                .nullable(),
              directCategoryIds: z.array(z.string()).max(2000).optional(),
              variantName: z
                .string()
                .describe('Variant name.')
                .min(1)
                .max(250)
                .optional()
                .nullable(),
              variantSku: z
                .string()
                .describe('Variant SKU (stock keeping unit).')
                .min(1)
                .max(40)
                .optional()
                .nullable(),
              variantVisible: z
                .boolean()
                .describe('Whether the variant is visible in the store.')
                .optional()
                .nullable(),
            })
            .describe('Associated product and variant details.')
            .optional(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe(
              'Custom field data for the inventory item object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
            )
            .optional(),
        }),
        z.xor([
          z.object({
            inStock: z.never().optional(),
            quantity: z.never().optional(),
          }),
          z.object({
            quantity: z.never().optional(),
            inStock: z
              .boolean()
              .describe(
                "Indicates that inventory is tracked by status rather than quantity.\n\nWhen set to `true`, the item is marked as available for sale without tracking exact quantities.\nWhen set to `false`, the item is marked as out of stock.\nThis tracking method is useful for made-to-order products or items with unlimited inventory.\n\nWhen using this tracking method, `trackQuantity` is `false` and preorder limits aren't supported."
              ),
          }),
          z.object({
            inStock: z.never().optional(),
            quantity: z
              .number()
              .int()
              .describe(
                'Indicates that inventory is tracked by quantity.\n\nSet this field to the number of items currently in stock.\nThis tracking method is useful when you need to know exactly how many items are available.\n\nWhen using this tracking method, `trackQuantity` is `true`.\nQuantity can be negative when inventory is decremented for an order that has already been paid.'
              )
              .min(-99999)
              .max(99999),
          }),
        ])
      )
    )
    .min(1)
    .max(1000)
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('The number of items returned in this response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursors to navigate through result pages. Returned if cursor paging was used.'
        )
        .optional(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const SearchInventoryItemsRequest = z.object({
  search: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object.\n\nLearn more about [filtering](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#filters).'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(512)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(10)
          .optional(),
        aggregations: z
          .array(
            z.intersection(
              z.object({
                name: z
                  .string()
                  .describe(
                    'Aggregation name, returned in `aggregations.results.name`.'
                  )
                  .max(100)
                  .optional()
                  .nullable(),
                type: z
                  .enum([
                    'VALUE',
                    'RANGE',
                    'SCALAR',
                    'DATE_HISTOGRAM',
                    'NESTED',
                  ])
                  .optional(),
                fieldPath: z
                  .string()
                  .describe(
                    'Field to aggregate by. Use dot notation to specify a JSON path. For example, `order.address.streetName`.'
                  )
                  .max(200)
                  .optional(),
                groupBy: z
                  .intersection(
                    z.object({
                      name: z
                        .string()
                        .describe(
                          'Unique, caller-defined aggregation name, returned in `aggregations.results`.'
                        )
                        .max(100)
                        .optional()
                        .nullable(),
                      fieldPath: z
                        .string()
                        .describe('Field to aggregate by.')
                        .max(200)
                        .optional(),
                    }),
                    z.xor([
                      z.object({ value: z.never().optional() }),
                      z.object({
                        value: z
                          .intersection(
                            z.object({
                              sortType: z
                                .enum(['COUNT', 'VALUE'])
                                .describe('Sort type.')
                                .optional(),
                              sortDirection: z
                                .enum(['DESC', 'ASC'])
                                .describe('Sort direction.')
                                .optional(),
                              limit: z
                                .number()
                                .int()
                                .describe(
                                  'Number of aggregation results to return.\nMin: `1`\nMax: `250`\nDefault: `10`'
                                )
                                .optional()
                                .nullable(),
                              missingValues: z
                                .enum(['EXCLUDE', 'INCLUDE'])
                                .describe(
                                  'Whether to include or exclude missing values in the aggregation results.\nDefault: `EXCLUDE`.'
                                )
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                includeOptions: z.never().optional(),
                              }),
                              z.object({
                                includeOptions: z
                                  .object({
                                    addToBucket: z
                                      .string()
                                      .describe(
                                        'Specify a custom name for the bucket containing the missing values. Defaults are `"N/A"` for strings, `0` for integers, and `false` for booleans.'
                                      )
                                      .max(20)
                                      .optional(),
                                  })
                                  .describe(
                                    'Options for including missing values in results.'
                                  ),
                              }),
                            ])
                          )
                          .describe('Value aggregation configuration.'),
                      }),
                    ])
                  )
                  .describe('Deprecated. Use `nested` instead.')
                  .optional(),
              }),
              z.xor([
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                }),
                z.object({
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                  value: z
                    .intersection(
                      z.object({
                        sortType: z
                          .enum(['COUNT', 'VALUE'])
                          .describe('Sort type.')
                          .optional(),
                        sortDirection: z
                          .enum(['DESC', 'ASC'])
                          .describe('Sort direction.')
                          .optional(),
                        limit: z
                          .number()
                          .int()
                          .describe(
                            'Number of aggregation results to return.\nMin: `1`\nMax: `250`\nDefault: `10`'
                          )
                          .optional()
                          .nullable(),
                        missingValues: z
                          .enum(['EXCLUDE', 'INCLUDE'])
                          .describe(
                            'Whether to include or exclude missing values in the aggregation results.\nDefault: `EXCLUDE`.'
                          )
                          .optional(),
                      }),
                      z.xor([
                        z.object({ includeOptions: z.never().optional() }),
                        z.object({
                          includeOptions: z
                            .object({
                              addToBucket: z
                                .string()
                                .describe(
                                  'Specify a custom name for the bucket containing the missing values. Defaults are `"N/A"` for strings, `0` for integers, and `false` for booleans.'
                                )
                                .max(20)
                                .optional(),
                            })
                            .describe(
                              'Options for including missing values in results.'
                            ),
                        }),
                      ])
                    )
                    .describe(
                      'A value aggregation calculates metrics such as count for specific fields within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of products for each price listed in the store.'
                    ),
                }),
                z.object({
                  value: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                  range: z
                    .object({
                      buckets: z
                        .array(
                          z.object({
                            from: z
                              .number()
                              .describe(
                                'Inclusive lower bound of the range. Required if `to` is not provided.'
                              )
                              .optional()
                              .nullable(),
                            to: z
                              .number()
                              .describe(
                                'Exclusive upper bound of the range. Required if `from` is not provided.'
                              )
                              .optional()
                              .nullable(),
                          })
                        )
                        .max(50)
                        .optional(),
                    })
                    .describe(
                      'A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more.'
                    ),
                }),
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z.never().optional(),
                  scalar: z
                    .object({
                      type: z
                        .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                        .describe('Operator type for the scalar aggregation.')
                        .optional(),
                    })
                    .describe(
                      'A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`.'
                    ),
                }),
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  nested: z.never().optional(),
                  dateHistogram: z
                    .object({
                      interval: z
                        .enum([
                          'YEAR',
                          'MONTH',
                          'WEEK',
                          'DAY',
                          'HOUR',
                          'MINUTE',
                          'SECOND',
                        ])
                        .describe('Interval for date histogram aggregation.')
                        .optional(),
                    })
                    .describe(
                      'A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.) For example, use a date histogram to find how many reservations have been made at a restaurant each week.'
                    ),
                }),
                z.object({
                  value: z.never().optional(),
                  range: z.never().optional(),
                  scalar: z.never().optional(),
                  dateHistogram: z.never().optional(),
                  nested: z
                    .object({
                      nestedAggregations: z
                        .array(
                          z.intersection(
                            z.object({
                              name: z
                                .string()
                                .describe(
                                  'Unique, caller-defined aggregation name, returned in `aggregations.results`.'
                                )
                                .max(100)
                                .optional()
                                .nullable(),
                              type: z
                                .enum([
                                  'VALUE',
                                  'RANGE',
                                  'SCALAR',
                                  'DATE_HISTOGRAM',
                                ])
                                .optional(),
                              fieldPath: z
                                .string()
                                .describe(
                                  'Field to aggregate by. Use dot notation to specify a JSON path. For example, `order.address.streetName`.'
                                )
                                .max(200)
                                .optional(),
                            }),
                            z.xor([
                              z.object({
                                value: z.never().optional(),
                                range: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z.never().optional(),
                              }),
                              z.object({
                                range: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z.never().optional(),
                                value: z
                                  .intersection(
                                    z.object({
                                      sortType: z
                                        .enum(['COUNT', 'VALUE'])
                                        .describe('Sort type.')
                                        .optional(),
                                      sortDirection: z
                                        .enum(['DESC', 'ASC'])
                                        .describe('Sort direction.')
                                        .optional(),
                                      limit: z
                                        .number()
                                        .int()
                                        .describe(
                                          'Number of aggregation results to return.\nMin: `1`\nMax: `250`\nDefault: `10`'
                                        )
                                        .optional()
                                        .nullable(),
                                      missingValues: z
                                        .enum(['EXCLUDE', 'INCLUDE'])
                                        .describe(
                                          'Whether to include or exclude missing values in the aggregation results.\nDefault: `EXCLUDE`.'
                                        )
                                        .optional(),
                                    }),
                                    z.xor([
                                      z.object({
                                        includeOptions: z.never().optional(),
                                      }),
                                      z.object({
                                        includeOptions: z
                                          .object({
                                            addToBucket: z
                                              .string()
                                              .describe(
                                                'Specify a custom name for the bucket containing the missing values. Defaults are `"N/A"` for strings, `0` for integers, and `false` for booleans.'
                                              )
                                              .max(20)
                                              .optional(),
                                          })
                                          .describe(
                                            'Options for including missing values in results.'
                                          ),
                                      }),
                                    ])
                                  )
                                  .describe(
                                    "A value aggregation calculates the distribution of a specific field's values within a dataset, providing insights into the overall distribution and key statistics of those values. For example, use a value aggregation to get the number (count) of orders for each order status."
                                  ),
                              }),
                              z.object({
                                value: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z.never().optional(),
                                range: z
                                  .object({
                                    buckets: z
                                      .array(
                                        z.object({
                                          from: z
                                            .number()
                                            .describe(
                                              'Inclusive lower bound of the range. Required if `to` is not provided.'
                                            )
                                            .optional()
                                            .nullable(),
                                          to: z
                                            .number()
                                            .describe(
                                              'Exclusive upper bound of the range. Required if `from` is not provided.'
                                            )
                                            .optional()
                                            .nullable(),
                                        })
                                      )
                                      .max(50)
                                      .optional(),
                                  })
                                  .describe(
                                    'A range aggregation calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. For example, use a range aggregation to compare the number of reservations made for parties of 4 or less to the number of reservations made for parties with 5 or more. If ranges overlap, a record that fits more than one range will only be counted in the first range that matches the criteria.'
                                  ),
                              }),
                              z.object({
                                value: z.never().optional(),
                                range: z.never().optional(),
                                dateHistogram: z.never().optional(),
                                scalar: z
                                  .object({
                                    type: z
                                      .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                                      .describe(
                                        'Operator type for the scalar aggregation.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'A scalar aggregation calculates a single numerical value from a dataset, summarizing the dataset into one key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`.'
                                  ),
                              }),
                              z.object({
                                value: z.never().optional(),
                                range: z.never().optional(),
                                scalar: z.never().optional(),
                                dateHistogram: z
                                  .object({
                                    interval: z
                                      .enum([
                                        'YEAR',
                                        'MONTH',
                                        'WEEK',
                                        'DAY',
                                        'HOUR',
                                        'MINUTE',
                                        'SECOND',
                                      ])
                                      .describe(
                                        'Interval for date histogram aggregation.'
                                      )
                                      .optional(),
                                  })
                                  .describe(
                                    'A date histogram calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.). For example, use a date histogram to determine how many reservations have been made at a restaurant each week. If ranges overlap, a record that fits more than one range will only be counted in the first range that matches the criteria.'
                                  ),
                              }),
                            ])
                          )
                        )
                        .min(2)
                        .max(3)
                        .optional(),
                    })
                    .describe(
                      'A nested aggregation is applied within the results of another aggregation. Rather than aggregating directly on the primary dataset, first group data using one aggregation and then apply another aggregation within each group. It allows for more complex analyses where you can summarize data at different levels of detail or hierarchy. For example, to get the number of products that are in stock and out of stock for each price listed, first perform a value aggregation on `discountedPriceNumeric`, and a second value aggregation on `inStock`.'
                    ),
                }),
              ])
            )
          )
          .max(10)
          .optional(),
        search: z
          .object({
            mode: z.enum(['OR', 'AND']).optional(),
            expression: z
              .string()
              .describe('Search term or expression.')
              .max(100)
              .optional()
              .nullable(),
            fields: z.array(z.string()).max(20).optional(),
            fuzzy: z
              .boolean()
              .describe(
                'Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions.'
              )
              .optional(),
          })
          .describe('Free text to match in searchable fields.')
          .optional(),
        timeZone: z
          .string()
          .describe(
            'Time zone to adjust date-time-based filters and aggregations, in ISO 8601 (including offsets) or IANA time zone database (including time zone IDs) format.\nApplies to all relevant filters and aggregations, unless overridden by providing timestamps including time zone. For example, "2023-12-20T10:52:34.795Z".'
          )
          .max(50)
          .optional()
          .nullable(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Maximum number of items to return in the results.')
                .min(0)
                .max(1000)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nPass the relevant cursor token from the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor paging options.\n\nLearn more about [cursor paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#cursor-paging).'
            ),
        }),
      ])
    )
    .describe('Search options.'),
});
export const SearchInventoryItemsResponse = z.object({
  inventoryItems: z
    .array(
      z.intersection(
        z.object({
          _id: z
            .string()
            .describe('Inventory item ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          revision: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Revision number, which increments by 1 each time the inventory item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the inventory item.\n\nIgnored when creating an inventory item.'
            )
            .optional()
            .nullable(),
          _createdDate: z
            .date()
            .describe('Date and time the inventory item was created.')
            .optional()
            .nullable(),
          _updatedDate: z
            .date()
            .describe('Date and time the inventory item was last updated.')
            .optional()
            .nullable(),
          variantId: z
            .string()
            .describe('Variant ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          locationId: z
            .string()
            .describe(
              "Stores location ID. If not specified when creating an inventory item, the store's default location is used."
            )
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional()
            .nullable(),
          productId: z
            .string()
            .describe('Product ID.')
            .min(1)
            .max(36)
            .optional(),
          trackQuantity: z
            .boolean()
            .describe('Whether the quantity is being tracked.')
            .optional(),
          availabilityStatus: z
            .enum(['OUT_OF_STOCK', 'IN_STOCK', 'PREORDER'])
            .describe('Inventory item availability status.')
            .optional(),
          preorderInfo: z
            .object({
              enabled: z
                .boolean()
                .describe(
                  "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
                )
                .optional()
                .nullable(),
              message: z
                .string()
                .describe(
                  'Message displayed to customers when the item is out of stock and preorder is enabled.'
                )
                .min(1)
                .max(250)
                .optional()
                .nullable(),
              limit: z
                .number()
                .int()
                .describe(
                  'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
                )
                .min(0)
                .max(100000)
                .optional()
                .nullable(),
              counter: z
                .number()
                .int()
                .describe(
                  'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
                )
                .min(0)
                .max(99999)
                .optional()
                .nullable(),
              quantity: z
                .number()
                .int()
                .describe(
                  'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
                )
                .optional()
                .nullable(),
            })
            .describe(
              "Item preorder info.\n\nPreorder settings are configured per inventory item, so each variant-location combination can have its own preorder configuration.\n\n> **Note:** The product entity's `inventory.preorderStatus` and `inventory.preorderAvailability` fields reflect only the default location's preorder state."
            )
            .optional(),
          product: z
            .object({
              name: z
                .string()
                .describe('Product name.')
                .max(80)
                .optional()
                .nullable(),
              directCategoryIds: z.array(z.string()).max(2000).optional(),
              variantName: z
                .string()
                .describe('Variant name.')
                .min(1)
                .max(250)
                .optional()
                .nullable(),
              variantSku: z
                .string()
                .describe('Variant SKU (stock keeping unit).')
                .min(1)
                .max(40)
                .optional()
                .nullable(),
              variantVisible: z
                .boolean()
                .describe('Whether the variant is visible in the store.')
                .optional()
                .nullable(),
            })
            .describe('Associated product and variant details.')
            .optional(),
          extendedFields: z
            .object({
              namespaces: z
                .record(z.string(), z.record(z.string(), z.any()))
                .describe(
                  'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                )
                .optional(),
            })
            .describe(
              'Custom field data for the inventory item object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
            )
            .optional(),
        }),
        z.xor([
          z.object({
            inStock: z.never().optional(),
            quantity: z.never().optional(),
          }),
          z.object({
            quantity: z.never().optional(),
            inStock: z
              .boolean()
              .describe(
                "Indicates that inventory is tracked by status rather than quantity.\n\nWhen set to `true`, the item is marked as available for sale without tracking exact quantities.\nWhen set to `false`, the item is marked as out of stock.\nThis tracking method is useful for made-to-order products or items with unlimited inventory.\n\nWhen using this tracking method, `trackQuantity` is `false` and preorder limits aren't supported."
              ),
          }),
          z.object({
            inStock: z.never().optional(),
            quantity: z
              .number()
              .int()
              .describe(
                'Indicates that inventory is tracked by quantity.\n\nSet this field to the number of items currently in stock.\nThis tracking method is useful when you need to know exactly how many items are available.\n\nWhen using this tracking method, `trackQuantity` is `true`.\nQuantity can be negative when inventory is decremented for an order that has already been paid.'
              )
              .min(-99999)
              .max(99999),
          }),
        ])
      )
    )
    .min(1)
    .max(1000)
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in current page.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe(
              'Cursor string pointing to the next page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to the previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe(
          'Cursor strings that point to the next page, previous page, or both.'
        )
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Whether there are more pages to retrieve following the current page.\n\n+ `true`: Another page of results can be retrieved.\n+ `false`: This is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
  aggregationData: z
    .object({
      results: z
        .array(
          z.intersection(
            z.object({
              name: z
                .string()
                .describe(
                  'Aggregation name, returned in `aggregations.results.name`.'
                )
                .max(100)
                .optional(),
              type: z
                .enum(['VALUE', 'RANGE', 'SCALAR', 'DATE_HISTOGRAM', 'NESTED'])
                .describe(
                  'Aggregation type. Must align with the corresponding aggregation field.'
                )
                .optional(),
              fieldPath: z
                .string()
                .describe(
                  'Field to aggregate by. Use dot notation to specify a JSON path. For example, `order.address.streetName`.'
                )
                .max(200)
                .optional(),
            }),
            z.xor([
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
              }),
              z.object({
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                values: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Value of the field.')
                            .max(100)
                            .optional(),
                          count: z
                            .number()
                            .int()
                            .describe('Number of entities with this value.')
                            .optional(),
                        })
                      )
                      .max(250)
                      .optional(),
                  })
                  .describe('Value aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                ranges: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          from: z
                            .number()
                            .describe('Inclusive lower bound of the range.')
                            .optional()
                            .nullable(),
                          to: z
                            .number()
                            .describe('Exclusive upper bound of the range.')
                            .optional()
                            .nullable(),
                          count: z
                            .number()
                            .int()
                            .describe('Total number of entities in this range.')
                            .optional(),
                        })
                      )
                      .max(50)
                      .optional(),
                  })
                  .describe('Range aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                scalar: z
                  .object({
                    type: z
                      .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                      .describe('Type of scalar aggregation.')
                      .optional(),
                    value: z
                      .number()
                      .describe('Value of the scalar aggregation.')
                      .optional(),
                  })
                  .describe('Scalar aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z.never().optional(),
                groupedByValue: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Value of the field.')
                            .max(1000)
                            .optional(),
                          nestedResults: z
                            .intersection(
                              z.object({
                                name: z
                                  .string()
                                  .describe(
                                    'Unique, caller-defined aggregation name, identifiable by the requested aggregation `name`.'
                                  )
                                  .max(100)
                                  .optional(),
                                type: z
                                  .enum([
                                    'VALUE',
                                    'RANGE',
                                    'SCALAR',
                                    'DATE_HISTOGRAM',
                                    'NESTED',
                                  ])
                                  .describe('Aggregation type.')
                                  .optional(),
                                fieldPath: z
                                  .string()
                                  .describe(
                                    'Field which the data was aggregated by.'
                                  )
                                  .max(200)
                                  .optional(),
                              }),
                              z.xor([
                                z.object({
                                  values: z.never().optional(),
                                  ranges: z.never().optional(),
                                  scalar: z.never().optional(),
                                }),
                                z.object({
                                  ranges: z.never().optional(),
                                  scalar: z.never().optional(),
                                  values: z
                                    .object({
                                      results: z
                                        .array(
                                          z.object({
                                            value: z
                                              .string()
                                              .describe('Value of the field.')
                                              .max(100)
                                              .optional(),
                                            count: z
                                              .number()
                                              .int()
                                              .describe(
                                                'Number of entities with this value.'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .max(250)
                                        .optional(),
                                    })
                                    .describe('Value aggregation results.'),
                                }),
                                z.object({
                                  values: z.never().optional(),
                                  scalar: z.never().optional(),
                                  ranges: z
                                    .object({
                                      results: z
                                        .array(
                                          z.object({
                                            from: z
                                              .number()
                                              .describe(
                                                'Inclusive lower bound of the range.'
                                              )
                                              .optional()
                                              .nullable(),
                                            to: z
                                              .number()
                                              .describe(
                                                'Exclusive upper bound of the range.'
                                              )
                                              .optional()
                                              .nullable(),
                                            count: z
                                              .number()
                                              .int()
                                              .describe(
                                                'Total number of entities in this range.'
                                              )
                                              .optional(),
                                          })
                                        )
                                        .max(50)
                                        .optional(),
                                    })
                                    .describe('Range aggregation results.'),
                                }),
                                z.object({
                                  values: z.never().optional(),
                                  ranges: z.never().optional(),
                                  scalar: z
                                    .object({
                                      type: z
                                        .enum(['COUNT_DISTINCT', 'MIN', 'MAX'])
                                        .describe('Type of scalar aggregation.')
                                        .optional(),
                                      value: z
                                        .number()
                                        .describe(
                                          'Value of the scalar aggregation.'
                                        )
                                        .optional(),
                                    })
                                    .describe('Scalar aggregation results.'),
                                }),
                              ])
                            )
                            .describe('Nested aggregations.')
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                  })
                  .describe('Group by value aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                nested: z.never().optional(),
                dateHistogram: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          value: z
                            .string()
                            .describe('Date in ISO 8601 format.')
                            .max(100)
                            .optional(),
                          count: z
                            .number()
                            .int()
                            .describe('Count of documents in the bucket.')
                            .optional(),
                        })
                      )
                      .max(200)
                      .optional(),
                  })
                  .describe('Date histogram aggregation results.'),
              }),
              z.object({
                values: z.never().optional(),
                ranges: z.never().optional(),
                scalar: z.never().optional(),
                groupedByValue: z.never().optional(),
                dateHistogram: z.never().optional(),
                nested: z
                  .object({
                    results: z
                      .array(
                        z.object({
                          results: z
                            .record(
                              z.string(),
                              z.intersection(
                                z.object({}),
                                z.xor([
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                  }),
                                  z.object({
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    value: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe('Value of the field.')
                                          .max(1000)
                                          .optional(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Number of entities with this value.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Value aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    range: z
                                      .object({
                                        from: z
                                          .number()
                                          .describe(
                                            'Inclusive lower bound of the range.'
                                          )
                                          .optional()
                                          .nullable(),
                                        to: z
                                          .number()
                                          .describe(
                                            'Exclusive upper bound of the range.'
                                          )
                                          .optional()
                                          .nullable(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Total number of entities in this range.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe('Range aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    dateHistogram: z.never().optional(),
                                    scalar: z
                                      .object({
                                        value: z
                                          .number()
                                          .describe(
                                            'Value of the scalar aggregation.'
                                          )
                                          .optional(),
                                      })
                                      .describe('Scalar aggregation result.'),
                                  }),
                                  z.object({
                                    value: z.never().optional(),
                                    range: z.never().optional(),
                                    scalar: z.never().optional(),
                                    dateHistogram: z
                                      .object({
                                        value: z
                                          .string()
                                          .describe('Value of the field.')
                                          .max(1000)
                                          .optional(),
                                        count: z
                                          .number()
                                          .int()
                                          .describe(
                                            'Number of entities with this value.'
                                          )
                                          .optional()
                                          .nullable(),
                                      })
                                      .describe(
                                        'Date histogram aggregation result.'
                                      ),
                                  }),
                                ])
                              )
                            )
                            .describe('List of nested aggregations.')
                            .optional(),
                        })
                      )
                      .max(1000)
                      .optional(),
                  })
                  .describe('Nested aggregation results.'),
              }),
            ])
          )
        )
        .max(10000)
        .optional(),
    })
    .describe('Aggregation data.')
    .optional(),
});
export const BulkDecrementInventoryItemsRequest = z.object({
  decrementData: z
    .array(
      z.object({
        inventoryItemId: z
          .string()
          .describe('Inventory item ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          ),
        decrementBy: z
          .number()
          .int()
          .describe('Amount to decrement by.')
          .min(1)
          .max(99999),
        preorderRequest: z
          .boolean()
          .describe(
            "Whether the request to decrement the inventory item's quantity was made as part of a purchase that includes preorder items.\n\n+ If `true` and the item is available for preorder in the default location, negative inventory quantity is allowed.\n+ If `false` and the item is not available for preorder, negative inventory is not allowed."
          )
          .optional(),
      })
    )
    .min(1)
    .max(300),
  options: z
    .object({
      restrictInventory: z
        .boolean()
        .describe(
          'Whether inventory is restricted from going below zero.\n\nDefault: `true` - inventory does not go below zero.'
        )
        .optional()
        .nullable(),
      returnEntity: z
        .boolean()
        .describe(
          'Whether to return the full inventory item entities in the response.\n\nDefault: `false`'
        )
        .optional(),
      reason: z.enum(['ORDER', 'MANUAL', 'REVERT_INVENTORY_CHANGE']).optional(),
    })
    .optional(),
});
export const BulkDecrementInventoryItemsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Bulk action metadata for inventory item.')
          .optional(),
        item: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Inventory item ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the inventory item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the inventory item.\n\nIgnored when creating an inventory item.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Date and time the inventory item was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the inventory item was last updated.')
                .optional()
                .nullable(),
              variantId: z
                .string()
                .describe('Variant ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              locationId: z
                .string()
                .describe(
                  "Stores location ID. If not specified when creating an inventory item, the store's default location is used."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              productId: z
                .string()
                .describe('Product ID.')
                .min(1)
                .max(36)
                .optional(),
              trackQuantity: z
                .boolean()
                .describe('Whether the quantity is being tracked.')
                .optional(),
              availabilityStatus: z
                .enum(['OUT_OF_STOCK', 'IN_STOCK', 'PREORDER'])
                .describe('Inventory item availability status.')
                .optional(),
              preorderInfo: z
                .object({
                  enabled: z
                    .boolean()
                    .describe(
                      "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
                    )
                    .optional()
                    .nullable(),
                  message: z
                    .string()
                    .describe(
                      'Message displayed to customers when the item is out of stock and preorder is enabled.'
                    )
                    .min(1)
                    .max(250)
                    .optional()
                    .nullable(),
                  limit: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
                    )
                    .min(0)
                    .max(100000)
                    .optional()
                    .nullable(),
                  counter: z
                    .number()
                    .int()
                    .describe(
                      'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
                    )
                    .min(0)
                    .max(99999)
                    .optional()
                    .nullable(),
                  quantity: z
                    .number()
                    .int()
                    .describe(
                      'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Item preorder info.\n\nPreorder settings are configured per inventory item, so each variant-location combination can have its own preorder configuration.\n\n> **Note:** The product entity's `inventory.preorderStatus` and `inventory.preorderAvailability` fields reflect only the default location's preorder state."
                )
                .optional(),
              product: z
                .object({
                  name: z
                    .string()
                    .describe('Product name.')
                    .max(80)
                    .optional()
                    .nullable(),
                  directCategoryIds: z.array(z.string()).max(2000).optional(),
                  variantName: z
                    .string()
                    .describe('Variant name.')
                    .min(1)
                    .max(250)
                    .optional()
                    .nullable(),
                  variantSku: z
                    .string()
                    .describe('Variant SKU (stock keeping unit).')
                    .min(1)
                    .max(40)
                    .optional()
                    .nullable(),
                  variantVisible: z
                    .boolean()
                    .describe('Whether the variant is visible in the store.')
                    .optional()
                    .nullable(),
                })
                .describe('Associated product and variant details.')
                .optional(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Custom field data for the inventory item object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                inStock: z.never().optional(),
                quantity: z.never().optional(),
              }),
              z.object({
                quantity: z.never().optional(),
                inStock: z
                  .boolean()
                  .describe(
                    "Indicates that inventory is tracked by status rather than quantity.\n\nWhen set to `true`, the item is marked as available for sale without tracking exact quantities.\nWhen set to `false`, the item is marked as out of stock.\nThis tracking method is useful for made-to-order products or items with unlimited inventory.\n\nWhen using this tracking method, `trackQuantity` is `false` and preorder limits aren't supported."
                  ),
              }),
              z.object({
                inStock: z.never().optional(),
                quantity: z
                  .number()
                  .int()
                  .describe(
                    'Indicates that inventory is tracked by quantity.\n\nSet this field to the number of items currently in stock.\nThis tracking method is useful when you need to know exactly how many items are available.\n\nWhen using this tracking method, `trackQuantity` is `true`.\nQuantity can be negative when inventory is decremented for an order that has already been paid.'
                  )
                  .min(-99999)
                  .max(99999),
              }),
            ])
          )
          .describe(
            'Full inventory item entity.\n\nReturned only if `returnEntity: true` is passed in the request.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(300)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const BulkIncrementInventoryItemsRequest = z.object({
  incrementData: z
    .array(
      z.object({
        inventoryItemId: z
          .string()
          .describe('Inventory item ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          ),
        incrementBy: z
          .number()
          .int()
          .describe('Amount to increment by.')
          .min(1)
          .max(99999),
      })
    )
    .min(1)
    .max(300),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to return the full inventory item entities in the response.\n\nDefault: `false`'
        )
        .optional(),
      reason: z.enum(['ORDER', 'MANUAL', 'REVERT_INVENTORY_CHANGE']).optional(),
    })
    .optional(),
});
export const BulkIncrementInventoryItemsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Bulk action metadata for inventory item.')
          .optional(),
        item: z
          .intersection(
            z.object({
              _id: z
                .string()
                .describe('Inventory item ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              revision: z
                .string()
                .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                .describe(
                  'Revision number, which increments by 1 each time the inventory item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the inventory item.\n\nIgnored when creating an inventory item.'
                )
                .optional()
                .nullable(),
              _createdDate: z
                .date()
                .describe('Date and time the inventory item was created.')
                .optional()
                .nullable(),
              _updatedDate: z
                .date()
                .describe('Date and time the inventory item was last updated.')
                .optional()
                .nullable(),
              variantId: z
                .string()
                .describe('Variant ID.')
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional(),
              locationId: z
                .string()
                .describe(
                  "Stores location ID. If not specified when creating an inventory item, the store's default location is used."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              productId: z
                .string()
                .describe('Product ID.')
                .min(1)
                .max(36)
                .optional(),
              trackQuantity: z
                .boolean()
                .describe('Whether the quantity is being tracked.')
                .optional(),
              availabilityStatus: z
                .enum(['OUT_OF_STOCK', 'IN_STOCK', 'PREORDER'])
                .describe('Inventory item availability status.')
                .optional(),
              preorderInfo: z
                .object({
                  enabled: z
                    .boolean()
                    .describe(
                      "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
                    )
                    .optional()
                    .nullable(),
                  message: z
                    .string()
                    .describe(
                      'Message displayed to customers when the item is out of stock and preorder is enabled.'
                    )
                    .min(1)
                    .max(250)
                    .optional()
                    .nullable(),
                  limit: z
                    .number()
                    .int()
                    .describe(
                      'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
                    )
                    .min(0)
                    .max(100000)
                    .optional()
                    .nullable(),
                  counter: z
                    .number()
                    .int()
                    .describe(
                      'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
                    )
                    .min(0)
                    .max(99999)
                    .optional()
                    .nullable(),
                  quantity: z
                    .number()
                    .int()
                    .describe(
                      'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
                    )
                    .optional()
                    .nullable(),
                })
                .describe(
                  "Item preorder info.\n\nPreorder settings are configured per inventory item, so each variant-location combination can have its own preorder configuration.\n\n> **Note:** The product entity's `inventory.preorderStatus` and `inventory.preorderAvailability` fields reflect only the default location's preorder state."
                )
                .optional(),
              product: z
                .object({
                  name: z
                    .string()
                    .describe('Product name.')
                    .max(80)
                    .optional()
                    .nullable(),
                  directCategoryIds: z.array(z.string()).max(2000).optional(),
                  variantName: z
                    .string()
                    .describe('Variant name.')
                    .min(1)
                    .max(250)
                    .optional()
                    .nullable(),
                  variantSku: z
                    .string()
                    .describe('Variant SKU (stock keeping unit).')
                    .min(1)
                    .max(40)
                    .optional()
                    .nullable(),
                  variantVisible: z
                    .boolean()
                    .describe('Whether the variant is visible in the store.')
                    .optional()
                    .nullable(),
                })
                .describe('Associated product and variant details.')
                .optional(),
              extendedFields: z
                .object({
                  namespaces: z
                    .record(z.string(), z.record(z.string(), z.any()))
                    .describe(
                      'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                    )
                    .optional(),
                })
                .describe(
                  'Custom field data for the inventory item object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                inStock: z.never().optional(),
                quantity: z.never().optional(),
              }),
              z.object({
                quantity: z.never().optional(),
                inStock: z
                  .boolean()
                  .describe(
                    "Indicates that inventory is tracked by status rather than quantity.\n\nWhen set to `true`, the item is marked as available for sale without tracking exact quantities.\nWhen set to `false`, the item is marked as out of stock.\nThis tracking method is useful for made-to-order products or items with unlimited inventory.\n\nWhen using this tracking method, `trackQuantity` is `false` and preorder limits aren't supported."
                  ),
              }),
              z.object({
                inStock: z.never().optional(),
                quantity: z
                  .number()
                  .int()
                  .describe(
                    'Indicates that inventory is tracked by quantity.\n\nSet this field to the number of items currently in stock.\nThis tracking method is useful when you need to know exactly how many items are available.\n\nWhen using this tracking method, `trackQuantity` is `true`.\nQuantity can be negative when inventory is decremented for an order that has already been paid.'
                  )
                  .min(-99999)
                  .max(99999),
              }),
            ])
          )
          .describe(
            'Full inventory item entity.\n\nReturned only if `returnEntity: true` is passed in the request.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(300)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk action metadata.')
    .optional(),
});
export const BulkDecrementInventoryItemsByVariantAndLocationRequest = z.object({
  decrementData: z
    .array(
      z.object({
        variantId: z
          .string()
          .describe('Variant ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          ),
        locationId: z
          .string()
          .describe('Location ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        decrementBy: z
          .number()
          .int()
          .describe('Amount to decrement by.')
          .min(1)
          .max(99999),
        preorderRequest: z
          .boolean()
          .describe(
            "Whether the request to decrement the inventory item's quantity was made as part of a purchase that includes preorder items.\n\n+ If `true` and the item is available for preorder in the default location, negative inventory quantity is allowed.\n+ If `false` and the item is not available for preorder, negative inventory is not allowed."
          )
          .optional(),
      })
    )
    .min(1)
    .max(300),
  options: z
    .object({
      restrictInventory: z
        .boolean()
        .describe(
          'Whether to allow negative inventory following this decrement action.\n\nDefault: `false` (negative inventory is not allowed)'
        )
        .optional()
        .nullable(),
      returnEntity: z
        .boolean()
        .describe(
          'Whether to return the full inventory item entities in the response.\n\nDefault: `false`'
        )
        .optional(),
      reason: z.enum(['ORDER', 'MANUAL', 'REVERT_INVENTORY_CHANGE']).optional(),
    })
    .optional(),
});
export const BulkDecrementInventoryItemsByVariantAndLocationResponse = z.object(
  {
    results: z
      .array(
        z.object({
          itemMetadata: z
            .object({
              _id: z
                .string()
                .describe(
                  "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              originalIndex: z
                .number()
                .int()
                .describe(
                  'Index of the item within the request array. Allows for correlation between request and response items.'
                )
                .optional(),
              success: z
                .boolean()
                .describe(
                  'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
                )
                .optional(),
              error: z
                .object({
                  code: z.string().describe('Error code.').optional(),
                  description: z
                    .string()
                    .describe('Description of the error.')
                    .optional(),
                  data: z
                    .record(z.string(), z.any())
                    .describe('Data related to the error.')
                    .optional()
                    .nullable(),
                })
                .describe('Details about the error in case of failure.')
                .optional(),
            })
            .describe('Bulk action metadata for inventory item.')
            .optional(),
          item: z
            .intersection(
              z.object({
                _id: z
                  .string()
                  .describe('Inventory item ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                revision: z
                  .string()
                  .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                  .describe(
                    'Revision number, which increments by 1 each time the inventory item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the inventory item.\n\nIgnored when creating an inventory item.'
                  )
                  .optional()
                  .nullable(),
                _createdDate: z
                  .date()
                  .describe('Date and time the inventory item was created.')
                  .optional()
                  .nullable(),
                _updatedDate: z
                  .date()
                  .describe(
                    'Date and time the inventory item was last updated.'
                  )
                  .optional()
                  .nullable(),
                variantId: z
                  .string()
                  .describe('Variant ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                locationId: z
                  .string()
                  .describe(
                    "Stores location ID. If not specified when creating an inventory item, the store's default location is used."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                productId: z
                  .string()
                  .describe('Product ID.')
                  .min(1)
                  .max(36)
                  .optional(),
                trackQuantity: z
                  .boolean()
                  .describe('Whether the quantity is being tracked.')
                  .optional(),
                availabilityStatus: z
                  .enum(['OUT_OF_STOCK', 'IN_STOCK', 'PREORDER'])
                  .describe('Inventory item availability status.')
                  .optional(),
                preorderInfo: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
                      )
                      .optional()
                      .nullable(),
                    message: z
                      .string()
                      .describe(
                        'Message displayed to customers when the item is out of stock and preorder is enabled.'
                      )
                      .min(1)
                      .max(250)
                      .optional()
                      .nullable(),
                    limit: z
                      .number()
                      .int()
                      .describe(
                        'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
                      )
                      .min(0)
                      .max(100000)
                      .optional()
                      .nullable(),
                    counter: z
                      .number()
                      .int()
                      .describe(
                        'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
                      )
                      .min(0)
                      .max(99999)
                      .optional()
                      .nullable(),
                    quantity: z
                      .number()
                      .int()
                      .describe(
                        'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    "Item preorder info.\n\nPreorder settings are configured per inventory item, so each variant-location combination can have its own preorder configuration.\n\n> **Note:** The product entity's `inventory.preorderStatus` and `inventory.preorderAvailability` fields reflect only the default location's preorder state."
                  )
                  .optional(),
                product: z
                  .object({
                    name: z
                      .string()
                      .describe('Product name.')
                      .max(80)
                      .optional()
                      .nullable(),
                    directCategoryIds: z.array(z.string()).max(2000).optional(),
                    variantName: z
                      .string()
                      .describe('Variant name.')
                      .min(1)
                      .max(250)
                      .optional()
                      .nullable(),
                    variantSku: z
                      .string()
                      .describe('Variant SKU (stock keeping unit).')
                      .min(1)
                      .max(40)
                      .optional()
                      .nullable(),
                    variantVisible: z
                      .boolean()
                      .describe('Whether the variant is visible in the store.')
                      .optional()
                      .nullable(),
                  })
                  .describe('Associated product and variant details.')
                  .optional(),
                extendedFields: z
                  .object({
                    namespaces: z
                      .record(z.string(), z.record(z.string(), z.any()))
                      .describe(
                        'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                      )
                      .optional(),
                  })
                  .describe(
                    'Custom field data for the inventory item object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  inStock: z.never().optional(),
                  quantity: z.never().optional(),
                }),
                z.object({
                  quantity: z.never().optional(),
                  inStock: z
                    .boolean()
                    .describe(
                      "Indicates that inventory is tracked by status rather than quantity.\n\nWhen set to `true`, the item is marked as available for sale without tracking exact quantities.\nWhen set to `false`, the item is marked as out of stock.\nThis tracking method is useful for made-to-order products or items with unlimited inventory.\n\nWhen using this tracking method, `trackQuantity` is `false` and preorder limits aren't supported."
                    ),
                }),
                z.object({
                  inStock: z.never().optional(),
                  quantity: z
                    .number()
                    .int()
                    .describe(
                      'Indicates that inventory is tracked by quantity.\n\nSet this field to the number of items currently in stock.\nThis tracking method is useful when you need to know exactly how many items are available.\n\nWhen using this tracking method, `trackQuantity` is `true`.\nQuantity can be negative when inventory is decremented for an order that has already been paid.'
                    )
                    .min(-99999)
                    .max(99999),
                }),
              ])
            )
            .describe(
              'Full inventory item entity.\n\nReturned only if `returnEntity: true` is passed in the request.'
            )
            .optional(),
        })
      )
      .min(1)
      .max(300)
      .optional(),
    bulkActionMetadata: z
      .object({
        totalSuccesses: z
          .number()
          .int()
          .describe('Number of items that were successfully processed.')
          .optional(),
        totalFailures: z
          .number()
          .int()
          .describe("Number of items that couldn't be processed.")
          .optional(),
        undetailedFailures: z
          .number()
          .int()
          .describe(
            'Number of failures without details because detailed failure threshold was exceeded.'
          )
          .optional(),
      })
      .describe('Bulk action metadata.')
      .optional(),
  }
);
export const BulkIncrementInventoryItemsByVariantAndLocationRequest = z.object({
  incrementData: z
    .array(
      z.object({
        variantId: z
          .string()
          .describe('Variant ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          ),
        locationId: z
          .string()
          .describe('Location ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        incrementBy: z
          .number()
          .int()
          .describe('Amount to increment by.')
          .min(1)
          .max(99999),
      })
    )
    .min(1)
    .max(300),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether to return the full inventory item entities in the response.\n\nDefault: `false`'
        )
        .optional(),
      reason: z.enum(['ORDER', 'MANUAL', 'REVERT_INVENTORY_CHANGE']).optional(),
    })
    .optional(),
});
export const BulkIncrementInventoryItemsByVariantAndLocationResponse = z.object(
  {
    results: z
      .array(
        z.object({
          itemMetadata: z
            .object({
              _id: z
                .string()
                .describe(
                  "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
                )
                .regex(
                  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                  'Must be a valid GUID'
                )
                .optional()
                .nullable(),
              originalIndex: z
                .number()
                .int()
                .describe(
                  'Index of the item within the request array. Allows for correlation between request and response items.'
                )
                .optional(),
              success: z
                .boolean()
                .describe(
                  'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
                )
                .optional(),
              error: z
                .object({
                  code: z.string().describe('Error code.').optional(),
                  description: z
                    .string()
                    .describe('Description of the error.')
                    .optional(),
                  data: z
                    .record(z.string(), z.any())
                    .describe('Data related to the error.')
                    .optional()
                    .nullable(),
                })
                .describe('Details about the error in case of failure.')
                .optional(),
            })
            .describe('Bulk action metadata for inventory item.')
            .optional(),
          item: z
            .intersection(
              z.object({
                _id: z
                  .string()
                  .describe('Inventory item ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                revision: z
                  .string()
                  .regex(/^-?\d+$/, 'Must be a valid Int64 string')
                  .describe(
                    'Revision number, which increments by 1 each time the inventory item is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the inventory item.\n\nIgnored when creating an inventory item.'
                  )
                  .optional()
                  .nullable(),
                _createdDate: z
                  .date()
                  .describe('Date and time the inventory item was created.')
                  .optional()
                  .nullable(),
                _updatedDate: z
                  .date()
                  .describe(
                    'Date and time the inventory item was last updated.'
                  )
                  .optional()
                  .nullable(),
                variantId: z
                  .string()
                  .describe('Variant ID.')
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional(),
                locationId: z
                  .string()
                  .describe(
                    "Stores location ID. If not specified when creating an inventory item, the store's default location is used."
                  )
                  .regex(
                    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                    'Must be a valid GUID'
                  )
                  .optional()
                  .nullable(),
                productId: z
                  .string()
                  .describe('Product ID.')
                  .min(1)
                  .max(36)
                  .optional(),
                trackQuantity: z
                  .boolean()
                  .describe('Whether the quantity is being tracked.')
                  .optional(),
                availabilityStatus: z
                  .enum(['OUT_OF_STOCK', 'IN_STOCK', 'PREORDER'])
                  .describe('Inventory item availability status.')
                  .optional(),
                preorderInfo: z
                  .object({
                    enabled: z
                      .boolean()
                      .describe(
                        "Whether preorder is enabled for this inventory item.\n\n> **Note:** Preorder can't be enabled for digital products or products with subscriptions.\n\nDefault: `false`"
                      )
                      .optional()
                      .nullable(),
                    message: z
                      .string()
                      .describe(
                        'Message displayed to customers when the item is out of stock and preorder is enabled.'
                      )
                      .min(1)
                      .max(250)
                      .optional()
                      .nullable(),
                    limit: z
                      .number()
                      .int()
                      .describe(
                        'Maximum number of items that can be preordered after stock reaches zero.\n\nSupported only for inventory items with `trackQuantity = true`.\n\nDefault: `100000`'
                      )
                      .min(0)
                      .max(100000)
                      .optional()
                      .nullable(),
                    counter: z
                      .number()
                      .int()
                      .describe(
                        'Number of times this item has been preordered.\n\nSupported only for inventory items with `trackQuantity = true`.'
                      )
                      .min(0)
                      .max(99999)
                      .optional()
                      .nullable(),
                    quantity: z
                      .number()
                      .int()
                      .describe(
                        'Remaining quantity available for preorder.\n\nSupported only for items with `trackQuantity` set to `true`.'
                      )
                      .optional()
                      .nullable(),
                  })
                  .describe(
                    "Item preorder info.\n\nPreorder settings are configured per inventory item, so each variant-location combination can have its own preorder configuration.\n\n> **Note:** The product entity's `inventory.preorderStatus` and `inventory.preorderAvailability` fields reflect only the default location's preorder state."
                  )
                  .optional(),
                product: z
                  .object({
                    name: z
                      .string()
                      .describe('Product name.')
                      .max(80)
                      .optional()
                      .nullable(),
                    directCategoryIds: z.array(z.string()).max(2000).optional(),
                    variantName: z
                      .string()
                      .describe('Variant name.')
                      .min(1)
                      .max(250)
                      .optional()
                      .nullable(),
                    variantSku: z
                      .string()
                      .describe('Variant SKU (stock keeping unit).')
                      .min(1)
                      .max(40)
                      .optional()
                      .nullable(),
                    variantVisible: z
                      .boolean()
                      .describe('Whether the variant is visible in the store.')
                      .optional()
                      .nullable(),
                  })
                  .describe('Associated product and variant details.')
                  .optional(),
                extendedFields: z
                  .object({
                    namespaces: z
                      .record(z.string(), z.record(z.string(), z.any()))
                      .describe(
                        'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                      )
                      .optional(),
                  })
                  .describe(
                    'Custom field data for the inventory item object.\n\n[Extended fields](https://dev.wix.com/docs/build-apps/develop-your-app/extensions/backend-extensions/schema-plugins/about-schema-plugin-extensions) must be configured in the app dashboard before they can be accessed with API calls.'
                  )
                  .optional(),
              }),
              z.xor([
                z.object({
                  inStock: z.never().optional(),
                  quantity: z.never().optional(),
                }),
                z.object({
                  quantity: z.never().optional(),
                  inStock: z
                    .boolean()
                    .describe(
                      "Indicates that inventory is tracked by status rather than quantity.\n\nWhen set to `true`, the item is marked as available for sale without tracking exact quantities.\nWhen set to `false`, the item is marked as out of stock.\nThis tracking method is useful for made-to-order products or items with unlimited inventory.\n\nWhen using this tracking method, `trackQuantity` is `false` and preorder limits aren't supported."
                    ),
                }),
                z.object({
                  inStock: z.never().optional(),
                  quantity: z
                    .number()
                    .int()
                    .describe(
                      'Indicates that inventory is tracked by quantity.\n\nSet this field to the number of items currently in stock.\nThis tracking method is useful when you need to know exactly how many items are available.\n\nWhen using this tracking method, `trackQuantity` is `true`.\nQuantity can be negative when inventory is decremented for an order that has already been paid.'
                    )
                    .min(-99999)
                    .max(99999),
                }),
              ])
            )
            .describe(
              'Full inventory item entity.\n\nReturned only if `returnEntity: true` is passed in the request.'
            )
            .optional(),
        })
      )
      .min(1)
      .max(300)
      .optional(),
    bulkActionMetadata: z
      .object({
        totalSuccesses: z
          .number()
          .int()
          .describe('Number of items that were successfully processed.')
          .optional(),
        totalFailures: z
          .number()
          .int()
          .describe("Number of items that couldn't be processed.")
          .optional(),
        undetailedFailures: z
          .number()
          .int()
          .describe(
            'Number of failures without details because detailed failure threshold was exceeded.'
          )
          .optional(),
      })
      .describe(
        'Bulk action metadata details as: totalSuccess and totalFailure.'
      )
      .optional(),
  }
);
